import React, { useEffect, useState } from "react";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { Box } from "@mui/system";
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { toastMessage } from "utils/toastMessage";
import { Toaster } from "react-hot-toast";
import { getWearNatureData } from "_controllers/officer";
import dotenv from "dotenv";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CheckroomOutlinedIcon from "@mui/icons-material/CheckroomOutlined";
import authAxios from "setups/axios";
import { AddWearNatureModal, EditWearNatureModal, DeleteConfirmationModel } from "utils/helperComponent";
import { formateData } from "utils/helper";
import { PageHeader, SectionCard, EmptyState, TablePager } from "_shared/ui";

dotenv.config();

const Main = () => {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editItem, setEditItem] = useState("");
  const [addItem, setAddItem] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [wearNatureData, setWearNatureData] = useState([]);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletedItemId, setDeletedItemId] = useState("");

  const itemsPerPage = 10; // Adjust this based on your preference
  const totalPages = Math.ceil(wearNatureData.length / itemsPerPage);
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = wearNatureData.slice(indexOfFirstItem, indexOfLastItem);

  // Initialization state
  const fetchData = async () => {
    try {
      const response = await getWearNatureData();
      setWearNatureData(response.data);
      setLoading(false);
    } catch (error) {
      setMessage(error?.message);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  const confirmDelete = async (id) => {
    const response = await authAxios()({
      url: `/api/v1/admin/dash/wear-nature/${id}`,
      method: "DELETE",
    });
    toastMessage(response.data.message, "success");
    setMessage(response?.data.message);
    const updatedData = wearNatureData.filter((item) => item._id !== id);
    setWearNatureData(updatedData);
  };
  const handleDelete = async (id) => {
    setDeleteDialogOpen(true);
    setDeletedItemId(id);
  };
  const onEditSave = async (inputData, id) => {
    try {
      setLoading(true);
      const response = await authAxios()({
        url: `/api/v1/admin/dash/wear-nature/${id}`,
        method: "PUT",
        data: inputData,
      });
      toastMessage(response.data.message, "success");
      setMessage(response?.data.message);
      setWearNatureData((prevData) => {
        return prevData.map((item) => {
          if (item._id === id) {
            const { updatedAt } = response.data.data;
            inputData.updatedAt = updatedAt;
            return { ...item, ...inputData };
          }
          return item;
        });
      });
      setLoading(false);
    } catch (error) {
      setMessage(error?.message);
    }
  };
  const onAddSave = async (inputData) => {
    try {
      setLoading(true);
      const response = await authAxios()({
        url: `/api/v1/admin/dash/wear-nature`,
        method: "POST",
        data: inputData,
      });
      console.log("🚀 ~ file: WearNature.jsx:110 ~ response:", response.data);
      toastMessage(response.data.message, "success");
      setMessage(response?.data.message);
      const { createdAt, updatedAt } = response.data.data;
      inputData.createdAt = createdAt;
      inputData.updatedAt = updatedAt;
      setWearNatureData((prevData) => [inputData, ...prevData]);
      setLoading(false);
    } catch (error) {
      setMessage(error?.message);
    }
  };
  const handleCloseDialog = () => {
    setDeleteDialogOpen(false);
    setDeletedItemId("");
  };
  const onClose = () => {
    setIsEditModalOpen(false);
    setIsAddModalOpen(false);
    return;
  };

  return (
    <>
      <PageHeader
        title="Wear Nature"
        subtitle="Manage the wear nature list"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setAddItem(addItem);
              setIsAddModalOpen(true);
            }}>
            Add new
          </Button>
        }
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {isEditModalOpen ? (
            <EditWearNatureModal
              editItem={editItem}
              onSave={onEditSave}
              onClose={onClose}
            />
          ) : (
            ""
          )}
          {isAddModalOpen ? (
            <AddWearNatureModal
              addItem={addItem}
              onSave={onAddSave}
              onClose={onClose}
            />
          ) : (
            ""
          )}
          {isDeleteDialogOpen ? (
            <DeleteConfirmationModel
              onOpen={isDeleteDialogOpen}
              onClose={handleCloseDialog}
              confirmDelete={confirmDelete}
              deletedItemId={deletedItemId}
            />
          ) : (
            ""
          )}
          <SectionCard title="Wear Nature List" disablePadding>
            <TableContainer component={Box}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: "bolder" }}>
                      Name
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: "bolder" }}>
                      Description
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bolder" }}>
                      Created At
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bolder" }}>
                      Updated At
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bolder" }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentItems && currentItems?.length > 0 ? (
                    currentItems?.map((item) => (
                      <TableRow key={item._id} hover>
                        <TableCell align="left">{item?.name}</TableCell>
                        <TableCell align="left">{item?.description}</TableCell>
                        <TableCell align="center">{formateData(item.createdAt)}</TableCell>
                        <TableCell align="center">{formateData(item.updatedAt)}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", justifyContent: "space-evenly" }}>
                            <EditIcon
                              sx={{
                                cursor: "pointer",
                                color: "success.main",
                              }}
                              onClick={() => {
                                setEditItem(item);
                                setIsEditModalOpen(true);
                              }}
                            />
                            {deleteLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon
                                sx={{
                                  cursor: "pointer",
                                  color: "error.main",
                                }}
                                onClick={() => handleDelete(item._id)}
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ border: 0 }}>
                        <EmptyState
                          icon={CheckroomOutlinedIcon}
                          title="No Wear Nature Available"
                          description="Add a new wear nature entry to get started."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePager
              count={wearNatureData.length}
              totalPages={totalPages}
              page={currentPage}
              onPageChange={(p) => handlePageChange(null, p)}
            />
          </SectionCard>
        </>
      )}
    </>
  );
};

const WearNature = () => {
  return (
    <AccessController roles={["ADMIN"]}>
      <DashGridL2
        Top={TopBar}
        Main={Main}
      />
      <Toaster />
    </AccessController>
  );
};

export default WearNature;
